import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import Row from 'components/elements/Row'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import Stack from 'components/elements/Stack'
import ViewsIcon from 'assets/icons/webapp_blue_views.svg'
import CalendarIcon from 'assets/icons/webapp_blue_calendar.svg'
import { formatDate } from 'lib/formatting/datetime'
import { Caption, Body2, Body1 } from 'stories/elements/Typography/Text'
import { parseLocale } from 'lib/constants/localization'
import ProfilePicture from 'components/elements/ProfilePicture'
import { KnowerLink } from 'lib/features/Know/knower'
import { returnKnowerFollowerLabel } from 'lib/formatting/knows'
import OverhaulKnowSEOBreadcrumbs from '../overhaul/OverhaulKnowSEOBreadcrumbs'
import _useTranslation from 'lib/hooks/_useTranslation'
import HTMLArticleContent from 'components/feature/Article/HTMLArticleContent'
import { ActiveExperiments } from 'lib/features/activeExperiments'
import { CourseBadge, GradeBadge, SubjectBadge } from 'components/elements/Badge'
import LikeIcon from 'assets/ai-enhanced/heart.svg'
import { useState, useRef, useEffect } from 'react'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventKnowSummaryExpandClick } from 'lib/constants/AnalyticsEventType'
import AppStylePreview from './AppStylePreview'

const Container = styled.div<{
    isDarkTheme?: boolean
}>`
    height: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: visible;
    padding: 15px 5px 5px;
    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  * {
                      color: ${theme.colors.white};
                  }
              `
            : ''}

    @media (max-width: 1050px) {
        padding: 20px;
    }

    h1 {
        font-family: var(--font-poppins) !important;
        font-size: 30px;
        line-height: 38px;
        font-weight: 600;
        color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.headlineDark)};
        margin: 0;
        margin-bottom: 0px;
        text-align: left;
        max-width: 800px;
        @media (max-width: 1050px) {
            width: 100%;
            font-size: 24px;
            line-height: 32px;
        }
    }
    .breadcrumbs {
        padding-bottom: 20px;
        @media (max-width: 1050px) {
            padding: 0 0 5px 0;
        }
    }
    .know-actions {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        max-width: 1050px;
        padding: 0 10px;

        @media (max-width: 1050px) {
            padding: 0px;
        }
        @media (max-width: 400px) {
            padding: 0px;
        }
    }
    .stats {
        flex-wrap: wrap;
        path {
            fill: ${theme.colors.primaryGray};
        }
        .heart {
            path {
                stroke: ${theme.colors.primaryGray};
            }
        }
        span {
            padding: 0;
        }
    }
    .hero {
        width: 1050px;
        display: flex;
        justify-content: center;
        gap: 50px;
        @media (max-width: 1050px) {
            width: 100%;
            flex-direction: column;
        }

        .thumbnail {
            object-fit: cover;
            box-shadow: 0px 6px 24px 0px #0000000d;
            border-radius: ${theme.borderRadius.normal};
            user-select: none;
            -webkit-touch-callout: none;
            pointer-events: none;

            ${({ isDarkTheme }) =>
                !isDarkTheme
                    ? css`
                          border: 2px solid ${theme.colors.lightAccentBorder};
                      `
                    : css`
                          border: 2px solid ${theme.colors.darkThemePrimary};
                      `};
            @media (max-width: 400px) {
                width: 320px !important;
            }
        }
        .cover-wrapper {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
        }
        .thumbnail-cover {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            overflow: hidden;
            width: fit-content;
            border-radius: ${theme.borderRadius.normal};

            &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 75px;
                background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.3));
                pointer-events: none;
            }

            .stat-chip {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
                svg path {
                    stroke: white;
                }
                position: absolute;
                bottom: 10px;
                right: 10px;
                width: fit-content;
                height: fit-content;
                background: rgba(0, 0, 0, 0.6);
                border-radius: ${theme.borderRadius.content};
                padding: 7.5px 10px;
                pointer-events: none;
                z-index: 10;
            }
            img {
                object-position: top;
            }

            .button {
                svg path {
                    fill: ${theme.colors.white};
                }
                border: 2px solid #e5e7eb;
                width: fit-content;
                height: 50px;
                position: absolute;
                display: flex;
                align-items: center;
                justify-content: center;
                vertical-align: middle;
                gap: 10px;
                z-index: 10;
                border-radius: ${theme.borderRadius.normal};

                background: ${theme.colors.deepSeaBlue};
                border-radius: ${theme.borderRadius.huge};
                padding: 25px 25px;
            }
        }
        .hero-content {
            display: flex;
            flex-direction: column;
            gap: 15px;
            justify-content: center;
            .knower-stats {
                display: flex;
                gap: 12.5px;
                justify-content: start;
                align-items: center;
                flex-wrap: wrap;
            }
        }
    }
`

const HeroContent = styled.div<{ isMobile?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 15px;
    justify-content: center;
    min-height: ${({ isMobile }) => (isMobile ? '150px' : '200px')};
    width: 100%;
    .summary-content {
        min-height: ${({ isMobile }) => (isMobile ? '48px' : '60px')};
    }
    .knower-stats {
        min-height: 40px;
        display: flex;
        gap: 12.5px;
        justify-content: start;
        align-items: center;
        flex-wrap: wrap;
    }
`

const SummaryContentWrapper = styled.div<{ isMobile?: boolean; isCut?: boolean; isDarkTheme?: boolean }>`
    min-height: ${({ isMobile }) => (isMobile ? '48px' : '60px')};
    width: 100%;
    position: relative;

    ${({ isCut }) =>
        isCut &&
        css`
            max-height: 170px;
            overflow: hidden;
        `}
`

const ExpandSection = styled.div<{ isDarkTheme?: boolean }>`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;

    padding: 4px 0;

    span {
        color: ${theme.colors.white};
        margin-right: 4px;
    }
`

const ExpandButton = styled.button`
    background: none;
    border: none;
    p {
        color: ${theme.colors.white};
        font-family: var(--font-inter);
        font-weight: 600;
        font-size: 16px !important;
    }
    cursor: pointer;
    padding: 0 0 0 4px;
    display: inline;
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    onContentClick: () => void
    referrerScreen?: string
    isMobile?: boolean
    isDarkTheme?: boolean
    experiments: ActiveExperiments
}

const AiEnhancedPNGHeroSection = ({
    knowSeoInfo,
    onContentClick,
    referrerScreen,
    isMobile,
    isDarkTheme,
    experiments,
}: Props) => {
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const { know } = knowSeoInfo
    const [isExpanded, setIsExpanded] = useState(false)
    const [shouldShowExpand, setShouldShowExpand] = useState(true)
    const contentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setIsExpanded(false)
    }, [knowSeoInfo])

    useEffect(() => {
        const checkHeight = () => {
            if (contentRef.current) {
                const currentHeight = contentRef.current.scrollHeight
                setShouldShowExpand(currentHeight > 200)
            }
        }

        checkHeight()
        window.addEventListener('resize', checkHeight)
        return () => window.removeEventListener('resize', checkHeight)
    }, [knowSeoInfo.summary])

    const cutSummaryAfterFewLines = true

    const KnowerSection = () => (
        <KnowerLink knower={know.knower}>
            <div className="knower-stats">
                <Row>
                    <ProfilePicture
                        user={know.knower.user}
                        size={35}
                        hasBorder={!isDarkTheme}
                        borderColor={theme.colors.backgroundBasic}
                    />
                    <Body1 color={theme.colors.contentSecondary} fontFamily="var(--font-inter)" fontWeight={600}>
                        {know.knower.user.name ?? know.knower.user.username}
                    </Body1>
                </Row>
                <Body2 color={theme.colors.placeholderText} fontFamily="var(--font-inter)" fontWeight={400}>
                    {`@${know.knower.user.username}`}
                </Body2>
                <Body2 color={theme.colors.placeholderText} fontFamily="var(--font-inter)" fontWeight={400}>
                    {'·'}
                </Body2>
                <Row>
                    <Body2 color={theme.colors.placeholderText} fontWeight={400} fontFamily="var(--font-inter)">
                        {returnKnowerFollowerLabel(know, t, router.locale)}
                    </Body2>
                    <Body2 color={theme.colors.deepSeaBlue} fontWeight={500} fontFamily="var(--font-inter)">
                        {t('knows/knowDetailsFollowKnowerButton')}
                    </Body2>
                </Row>
            </div>
        </KnowerLink>
    )

    const onExpandClick = () => {
        setIsExpanded(true)
        trackEvent(AnalyticsEventKnowSummaryExpandClick, { know })
    }

    return (
        <Container isDarkTheme={isDarkTheme}>
            <section className="know-actions" data-nosnippet="true">
                <div className="hero">
                    <HeroContent isMobile={isMobile}>
                        {isMobile ? (
                            <>
                                <AppStylePreview knowSeoInfo={knowSeoInfo} onContentClick={onContentClick} />

                                <OverhaulKnowSEOBreadcrumbs
                                    knowSeoInfo={knowSeoInfo}
                                    referrerScreen={referrerScreen}
                                    minimalizeBreadCrumbs
                                />
                                <Stack gutter={10}>
                                    <h1>{knowSeoInfo.title}</h1>
                                </Stack>
                            </>
                        ) : null}
                        {!isMobile ? (
                            <>
                                <OverhaulKnowSEOBreadcrumbs
                                    knowSeoInfo={knowSeoInfo}
                                    referrerScreen={referrerScreen}
                                    minimalizeBreadCrumbs
                                />
                                <Stack gutter={10}>
                                    <h1>{knowSeoInfo.title}</h1>
                                </Stack>
                            </>
                        ) : null}
                        {!isMobile ? <KnowerSection /> : null}
                        <Stack gutter={15} className="summary-content">
                            {knowSeoInfo.summary ? (
                                <div
                                    style={{
                                        position: 'relative',
                                        paddingBottom: shouldShowExpand && !isExpanded ? '30px' : '0',
                                    }}
                                >
                                    <SummaryContentWrapper
                                        ref={contentRef}
                                        isMobile={isMobile}
                                        isCut={shouldShowExpand && !isExpanded && cutSummaryAfterFewLines}
                                        isDarkTheme={isDarkTheme}
                                    >
                                        <HTMLArticleContent contentHtml={knowSeoInfo.summary} isDarkTheme={isDarkTheme} />
                                    </SummaryContentWrapper>
                                    {cutSummaryAfterFewLines && shouldShowExpand && !isExpanded && (
                                        <ExpandSection isDarkTheme={isDarkTheme}>
                                            <span>...</span>
                                            <ExpandButton onClick={onExpandClick}>
                                                <Body2 fontFamily="var(--font-inter)" fontWeight={500}>
                                                    {tCommon('common/showMoreButtonText')}
                                                </Body2>
                                            </ExpandButton>
                                        </ExpandSection>
                                    )}
                                </div>
                            ) : (
                                <Body1 fontFamily="var(--font-inter)" fontWeight={400}>
                                    {knowSeoInfo.description}
                                </Body1>
                            )}
                        </Stack>

                        <Row gap={20} className="stats">
                            <Row gap={8}>
                                <CalendarIcon />
                                <Caption
                                    color={theme.colors.actionGray}
                                    fontFamily="var(--font-inter)"
                                    fontSize={14}
                                    fontWeight={500}
                                >
                                    {formatDate(knowSeoInfo.know.createdOn, parsedLocale.interfaceLanguageCode)}
                                </Caption>
                            </Row>
                            <Row gap={8}>
                                <ViewsIcon />
                                <Body2 color={theme.colors.actionGray} fontFamily="var(--font-inter)" fontWeight={500}>
                                    {knowSeoInfo.know.views}
                                </Body2>
                            </Row>
                            {isDarkTheme && !isMobile ? (
                                <>
                                    <GradeBadge grades={know.grades} isTransparent />
                                    {know.subjects?.length ? (
                                        <SubjectBadge subject={know.subjects[0]} isTransparent />
                                    ) : null}
                                    {know.courses?.length ? <CourseBadge course={know.courses[0]} isTransparent /> : null}

                                    <Row gap={8}>
                                        <LikeIcon className="heart" />
                                        <Body2
                                            color={theme.colors.actionGray}
                                            fontFamily="var(--font-inter)"
                                            fontWeight={500}
                                        >
                                            {knowSeoInfo.know.likes.toString()}
                                        </Body2>
                                    </Row>
                                </>
                            ) : null}
                        </Row>
                    </HeroContent>
                </div>
            </section>
        </Container>
    )
}

export default AiEnhancedPNGHeroSection
